import { structuredCardToWorldDocument } from "./card-adapter";
import { normalizeStoryMap } from "./maps";
import { createWorldCalendar, validateWorldCalendar, type WorldCalendarDefinition } from './calendar';
import type {
  AccountStoryDocument,
  CardCategoryDocument,
  CardTemplateDocument,
  StartSceneDocument,
  StructuredCardDocument,
} from "./product";
import type { ContextVisibility, JsonObject, JsonValue, WorldDocument } from "./types";

export interface SessionWorldBootstrapOptions {
  createdAt?: string;
  selectedStartSceneId?: string;
}

/** Selects the hot files for one turn without marking every NPC/location hot. */
export function selectActiveSessionDocumentIds(documents: readonly WorldDocument[], limit = 16): string[] {
  const existing = documents.filter((document) => !document.deletedAt);
  const seeds = existing.filter((document) => (
    document.kind === "world_clock"
    || (document.kind === "scene" && (document.runtimeState?.status ?? document.fields.status) === "active")
    || (document.kind === "character" && ((document.runtimeState?.role ?? document.fields.role) === "player_character" || (document.runtimeState?.isPlayer ?? document.fields.isPlayer) === true))
  ));
  const selected: string[] = [];
  const seen = new Set<string>();
  const add = (id: string) => {
    if (!id || seen.has(id) || !existing.some((document) => document.id === id)) return;
    seen.add(id); selected.push(id);
  };
  seeds.forEach((document) => add(document.id));
  for (const document of seeds) {
    document.references.filter((reference) => ["current_location", "current_scene", "located_at", "features"].includes(reference.relation)).forEach((reference) => add(reference.targetId));
    const locationValue = document.runtimeState?.currentLocationId ?? document.fields.currentLocationId;
    const currentLocationId = typeof locationValue === "string" ? locationValue : "";
    add(currentLocationId);
  }
  for (const doc of documents) if (doc.runtimeState?.sidebarPinned === true) add(doc.id);
  return selected.slice(0, Math.max(1, Math.min(64, Math.trunc(limit))));
}

const object = (value: unknown): Record<string, unknown> => value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};
const text = (value: unknown, fallback = "") => typeof value === "string" && value.trim() ? value.trim() : fallback;

function jsonValue(value: unknown): JsonValue {
  if (value == null || typeof value === "string" || typeof value === "number" || typeof value === "boolean") return value as JsonValue;
  if (Array.isArray(value)) return value.map(jsonValue);
  if (typeof value === "object") return Object.fromEntries(Object.entries(value as Record<string, unknown>).filter(([, item]) => item !== undefined).map(([key, item]) => [key, jsonValue(item)]));
  return String(value);
}

function safeId(value: string, fallback: string): string {
  const result = value.trim().toLowerCase().normalize("NFKD").replace(/[^\p{L}\p{N}_.:-]+/gu, "-").replace(/^-+|-+$/g, "");
  return result || fallback;
}

function designationKind(template: CardTemplateDocument | undefined, category: CardCategoryDocument | undefined): string {
  const raw = `${template?.designation ?? ""} ${template?.name ?? ""} ${category?.name ?? ""}`.toLowerCase();
  if (/character|persona|ตัวละคร|บุคคล/.test(raw)) return "character";
  if (/item|equipment|weapon|armor|ไอเทม|อุปกรณ์|อาวุธ|เกราะ/.test(raw)) return "item";
  if (/skill|ability|power|spell|สกิล|ทักษะ|ความสามารถ|เวท/.test(raw)) return "skill";
  if (/location|place|map|สถานที่|พื้นที่|แผนที่/.test(raw)) return "location";
  const designation = safeId(text(template?.designation), "custom_card");
  return designation === "none" ? "custom_card" : designation;
}

function createDocument(input: Omit<WorldDocument, "schemaVersion" | "revision" | "createdAt" | "updatedAt">, at: string): WorldDocument {
  return { ...input, schemaVersion: 1, revision: 1, createdAt: at, updatedAt: at };
}

function structuredCardDocument(
  storyId: string,
  card: StructuredCardDocument,
  template: CardTemplateDocument | undefined,
  category: CardCategoryDocument | undefined,
  at: string,
): WorldDocument {
  const kind = designationKind(template, category);
  if (template && category && template.craftSlug) {
    const document = structuredCardToWorldDocument(card, category, template, storyId, at);
    return { ...document, id: `${kind}:${safeId(card.id, "card")}`, kind, revision: 1 };
  }
  const fields = jsonValue({
    ...card.values,
    status: card.status,
    source: card.source,
    images: card.images,
    triggers: card.triggers,
    templateId: card.templateId,
    templateVersion: card.templateVersion,
  }) as JsonObject;
  return createDocument({
    id: `${kind}:${safeId(card.id, "card")}`,
    storyId,
    kind,
    title: text(card.title, `Untitled ${category?.name ?? template?.name ?? "card"}`),
    summary: text(card.summary, text(category?.description)),
    fields,
    references: [],
    visibility: card.contextVisibility ?? template?.contextVisibility ?? "searchable",
    displayVisibility: card.visibleToPlayers ? "player" : "creator",
    editableBy: ["creator", "gm", "system"],
    ...(category?.id || template?.id ? { fileTypeId: category?.id ?? template!.id } : {}),
    tags: card.triggers,
  }, at);
}

function selectedScene(bot: AccountStoryDocument, session: AccountStoryDocument, requestedId?: string): StartSceneDocument | undefined {
  const id = requestedId || text(session.startSceneId);
  return bot.startScenes.find((scene) => scene.id === id) ?? bot.startScenes.find((scene) => scene.enabled);
}

/**
 * Builds the canonical filesystem for a new play session. Presentation strings
 * stay in AccountStoryDocument; Orbit mutates these compact typed files instead.
 */
export function compileSessionWorldDocuments(
  bot: AccountStoryDocument,
  session: AccountStoryDocument,
  options: SessionWorldBootstrapOptions = {},
): WorldDocument[] {
  const at = options.createdAt ?? new Date(session.createdAt || Date.now()).toISOString();
  const storyId = session.id;
  const categories = new Map(bot.cardCategories.map((category) => [category.id, category]));
  const templates = new Map(bot.cardTemplates.map((template) => [template.id, template]));
  const files = bot.structuredCards.filter((card) => card.enabled).map((card) => structuredCardDocument(storyId, card, templates.get(card.templateId), categories.get(card.categoryId), at));
  const idMap = new Map(bot.structuredCards.filter(card => card.enabled).map((card, index) => [card.id, files[index]!.id]));
  const remap = (value: JsonValue): JsonValue => typeof value === "string" ? idMap.get(value) ?? value : Array.isArray(value) ? value.map(remap) : value && typeof value === "object" ? Object.fromEntries(Object.entries(value).map(([key, child]) => [key, remap(child)])) : value;
  for (const file of files) { file.fields = remap(file.fields) as JsonObject; file.references = file.references.map(reference => ({ ...reference, targetId: idMap.get(reference.targetId) ?? reference.targetId })); }
  const scene = selectedScene(bot, session, options.selectedStartSceneId);
  const locationId = `location:start-${safeId(scene?.id ?? "opening", "opening")}`;
  const sceneId = `scene:${safeId(scene?.id ?? "opening", "opening")}`;
  const clockId = `world_clock:${safeId(storyId, "session")}`;
  // A session carries its own definition. Editing the master never resets a running clock.
  const savedCalendar = (session.worldCalendar ?? bot.worldCalendar) as WorldCalendarDefinition;
  const calendar = validateWorldCalendar(savedCalendar).length ? createWorldCalendar(new Date(bot.createdAt || session.createdAt), `calendar:${bot.id}`) : structuredClone(savedCalendar);
  const playerId = `character:player-${safeId(text(session.playerName, "player"), "player")}`;
  const opening = text(scene?.openingScene, text(bot.openingScene));
  const generated = object(session.generatedCharacter);
  const characterFields = object(session.characterFields);
  const persona = text(session.personaMemory);
  const existingIds = new Set(files.map((file) => file.id));

  const ensure = (document: WorldDocument) => { if (!existingIds.has(document.id)) { files.push(document); existingIds.add(document.id); } };
  ensure(createDocument({
    id: locationId, storyId, kind: "location", fileTypeId: "core.location", title: text(scene?.title, "จุดเริ่มต้น"),
    summary: text(scene?.description, opening.slice(0, 280)),
    fields: jsonValue({ image: scene?.image ?? "" }) as JsonObject, runtimeState: { status: "active", discovered: true },
    references: [], visibility: "pinned", displayVisibility: "player", editableBy: ["creator", "gm", "system"],
  }, at));
  ensure(createDocument({
    id: playerId, storyId, kind: "character", fileTypeId: "core.character", title: text(session.playerName, text(generated.name, "ผู้เล่น")),
    summary: persona || text(generated.background, "ตัวละครของผู้เล่น"),
    fields: jsonValue({ ...characterFields, ...generated, personaMemory: persona }) as JsonObject, runtimeState: { role: "player_character", isPlayer: true, currentLocationId: locationId },
    references: [{ relation: "current_location", targetId: locationId }], visibility: "partial", displayVisibility: "player", editableBy: ["player", "creator", "gm", "system"],
  }, at));
  ensure(createDocument({
    id: sceneId, storyId, kind: "scene", fileTypeId: "core.scene", title: text(scene?.title, "ฉากเปิด"), summary: opening.slice(0, 500),
    fields: jsonValue({ opening }) as JsonObject, runtimeState: { status: "active", startedAt: at, consequences: [] },
    references: [{ relation: "located_at", targetId: locationId }, { relation: "features", targetId: playerId }],
    visibility: "pinned", displayVisibility: "player", editableBy: ["creator", "gm", "system"],
  }, at));
  const gameStartId = `game_start:${safeId(scene?.id ?? "start", "start")}`;
  ensure(createDocument({
    id: gameStartId, storyId, kind: "game_start", fileTypeId: "core.game_start", title: text(scene?.title, "จุดเริ่มต้นเรื่อง"),
    summary: opening.slice(0, 500),
    fields: jsonValue({
      openingProse: opening,
      startingLocationId: locationId,
      startingSceneId: sceneId,
    }) as JsonObject,
    runtimeState: { status: "active", triggeredAt: at },
    references: [{ relation: "starts_at", targetId: locationId }, { relation: "initiates_scene", targetId: sceneId }],
    visibility: "pinned", displayVisibility: "player", editableBy: ["creator", "gm", "system"],
  }, at));
  ensure(createDocument({
    id: clockId, storyId, kind: "world_clock", fileTypeId: "core.world_clock", title: "เวลาของเรื่อง", summary: "นาฬิกากลางสำหรับคุมลำดับเวลาของเรื่อง",
    fields: { calendarDefinition: jsonValue(calendar) }, runtimeState: { clockState: jsonValue(calendar.initial), elapsedSeconds: 0, elapsedMinutes: 0 }, references: [{ relation: "current_scene", targetId: sceneId }],
    visibility: "pinned", displayVisibility: "player", editableBy: ["creator", "gm", "system"],
  }, at));

  const profileIds = new Set(files.filter((file) => file.kind === "character").map((file) => file.id));
  for (const profile of bot.profiles.filter((item) => item.enabled)) {
    const id = `character:${safeId(profile.id || profile.name, "profile")}`;
    if (profileIds.has(id)) continue;
    files.push(createDocument({
      id, storyId, kind: "character", fileTypeId: "core.character", title: text(profile.name, "Untitled character"), summary: text(profile.details),
      fields: jsonValue({ type: profile.type, details: profile.details, images: profile.images, playable: profile.playable }) as JsonObject,
      references: [], visibility: profile.pinned ? "pinned" : "searchable", displayVisibility: profile.visibleToPlayers ? "player" : "creator",
      editableBy: ["creator", "gm", "system"], tags: profile.triggers.split(/[,\n]/).map((item) => item.trim()).filter(Boolean),
    }, at));
    profileIds.add(id);
  }

  for (const source of bot.storyMaps ?? []) {
    const map = normalizeStoryMap(structuredClone(source), storyId);
    map.storyId = storyId;
    const fileId = (id: string) => idMap.get(id) ?? id;
    if (map.rootLocationId) map.rootLocationId = fileId(map.rootLocationId);
    map.layers = map.layers.map(layer => ({ ...layer, ...(typeof layer.locationDocumentId === "string" ? { locationDocumentId: fileId(layer.locationDocumentId) } : {}) }));
    map.anchors = map.anchors.map(anchor => ({ ...anchor, locationDocumentId: fileId(anchor.locationDocumentId) }));
    if (map.areas) map.areas = map.areas.map(area => ({ ...area, locationDocumentId: fileId(area.locationDocumentId) }));
    if (map.tokens) map.tokens = map.tokens.map(token => ({ ...token, slug: fileId(token.slug), ...(token.link ? { link: fileId(token.link) } : {}) }));
    files.push(createDocument({ id: map.id, storyId, kind: "story_map", title: map.title, summary: "Scene map", fields: jsonValue(map) as JsonObject, references: [], visibility: "pinned", displayVisibility: "player", editableBy: ["creator", "gm", "system"] }, at));
  }
  return files;
}
